import type React from 'react';
import { useState } from 'react';
import titleTemplate from 'lib/title-template';
import Head from 'next/head';
import Alert from 'components/common/alert';
import * as FeedbackModel from 'models/feedback-model';
import Page from 'components/common/page';
import Button from 'components/common/button';
import type { NextPage } from 'next';
import { shallowEqual, useSelector } from 'react-redux';
import type { Store } from 'redux/store';
import { pageTitle } from 'css/marketing-pages';

const NAME = 'Contact Page';

const column = 'mx-auto max-w-162 px-4 py-16 max-md:py-10';
const label = 'mt-5 block text-sm font-semibold text-ink first:mt-0';

const ContactPage: NextPage = () => {
  const [req, setReq] = useState<'waiting' | 'success' | 'error'>('waiting');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const updateName = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value);
  };
  const updateEmail = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
  };
  const updateMessage = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setMessage(event.target.value);
  };
  const { data } = useSelector((state: Store) => state.auth, shallowEqual);
  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    try {
      await FeedbackModel.createFeedback({
        name: name,
        email: email,
        message: message,
        userAgent: navigator.userAgent,
        emailUsedAsLogin: data?.email,
      });
      setReq('success');
    } catch {
      setReq('error');
    }
  };

  if (req === 'error') {
    return (
      <Page pageName={NAME}>
        <div className={column}>
          <Alert type="danger">
            Your message was not sent due to an error.
          </Alert>
        </div>
      </Page>
    );
  }

  if (req === 'success') {
    return (
      <Page pageName={NAME}>
        <div className={column}>
          <Alert type="success">
            Thanks for contacting us! If you requested a response, we&apos;ll
            get back to you as soon as possible.
          </Alert>
        </div>
      </Page>
    );
  }

  return (
    <Page pageName={NAME}>
      <Head>
        <title>{titleTemplate('Contact')}</title>
        <meta
          name="description"
          content="Send a message to our team to share feedback on Bibcitation features, report problems or just say hello!"
        />
      </Head>
      <div className={column}>
        <h1 className={pageTitle}>Get in touch with us</h1>
        <p className="mt-4 mb-0 text-17 leading-[1.6] text-ink-2">
          Fill out the following form to send us a message.
        </p>
        <form
          className="mt-8 rounded-2xl border border-line bg-surface p-8 shadow-paper max-md:p-5"
          onSubmit={handleSubmit}
        >
          <label className={label}>
            Name
            <input
              type="text"
              placeholder="Name (optional)"
              className="mt-1.5 font-normal"
              onChange={updateName}
            />
          </label>
          <label className={label}>
            Email
            <input
              type="email"
              placeholder="Email (optional)"
              className="mt-1.5 font-normal"
              onChange={updateEmail}
            />
          </label>
          <label className={label}>
            Message
            <textarea
              placeholder="Message (required)"
              required
              className="mt-1.5 font-normal"
              onChange={updateMessage}
            />
          </label>
          <div className="mt-6">
            <Button type="brand" size="medium" onClick={handleSubmit}>
              Send Message
            </Button>
          </div>
        </form>
      </div>
    </Page>
  );
};

export default ContactPage;
